import React, { useMemo } from 'react';
import { View, RefreshControl, ScrollView } from 'react-native';

import { countDriverTickets, countDriverResolvedTickets } from '../../../common/utils/ticketList/countDriverTickets';
import sortServiceDockets from '../../../common/utils/serviceDocket/sortServiceDockets';
import { shouldMachineBeRepaired } from '../../../common/utils/serviceRuns/shouldMachineBeRepaired';
import { DetailContainer } from '../../containers';
import { Dialog } from '../../components';
import Text from '../../components/text/Text';
import { MachineChip, Tag } from '../../design/components';
import { useRefreshPage } from '../../hooks/useRefreshPage';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import TicketListView from './TicketListView';
import { EmptyListComponent } from '../../components/list/List';
import { useUserContext } from '../../../common/utils/context';


const TicketSummary = ({
	data: serviceRun,
	refetch,
}) => {
	const [refreshPage, isRefreshing] = useRefreshPage(refetch);
	const styles = useThemedStyles(createStyles);
	const { tenantSettings } = useUserContext();
	// filter serviceDocket where there is no tickets
	const sortedServiceDockets = useMemo(() => {
		const filteredDockets = serviceRun.serviceDockets
			.filter(serviceDocket => shouldMachineBeRepaired(serviceDocket.machineServiceType) &&
				serviceDocket.ticketList?.driverItems?.length);
		return [...filteredDockets].sort(sortServiceDockets);
	}, [serviceRun.serviceDockets]);

	if (!sortedServiceDockets?.length) {
		return <EmptyListComponent emptyListMessage="No open tickets for this service run" emptyListHint="Open tickets for this run's machines will appear here." />;
	}

	return (
		<>
			<ScrollView
				style={styles.scrollView}
				scrollIndicatorInsets={{ right: 1 }}
				refreshControl={(
					<RefreshControl
						refreshing={isRefreshing}
						onRefresh={refreshPage}
					/>
				)}
			>
				<View>
					{
						sortedServiceDockets.map((serviceDocket) => {
							const { place, location, assetId } = serviceDocket.machine;
							return (
								<View key={assetId}>
									{/* Site first, then the position and the machine number (SPEC decision 6). */}
									<View style={styles.machineHeader}>
										<View style={styles.machineTitles}>
											<Text style={styles.site}>{location?.name || place?.name}</Text>
											<View style={styles.positionRow}>
												{Boolean(place?.name && location?.name) && <Text style={styles.position}>{place.name}</Text>}
												<MachineChip number={assetId} />
											</View>
										</View>
										<Tag
											tone="primary"
											label={`Tickets ${countDriverResolvedTickets(serviceDocket.ticketList.driverItems, tenantSettings)}/${countDriverTickets(serviceDocket.ticketList.driverItems, tenantSettings)}`}
										/>
									</View>
									<View>
										<TicketListView
											items={serviceDocket.ticketList.driverItems}
											showMachineName={true}
											onAfterResolve={refetch}
										/>
									</View>
								</View>
							);
						})
					}
				</View>
			</ScrollView>
			<Dialog />
		</>
	);
};

const createStyles = ({ ui: { colors, space, type } }) => ({
	scrollView: {
		flex: 1,
		backgroundColor: colors.canvas,
	},
	machineHeader: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: space.md,
		paddingHorizontal: space.gutter,
		paddingTop: space.lg,
		paddingBottom: space.sm,
	},
	machineTitles: {
		flex: 1,
		gap: space.xs,
	},
	site: {
		...type.sectionTitle,
		color: colors.text,
	},
	positionRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: space.sm,
	},
	position: {
		...type.bodySmallStrong,
		color: colors.text,
	},
});

export default DetailContainer(TicketSummary);
